import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { feedbackAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquare, X, Bug, Lightbulb, HelpCircle, Heart, Send, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const TYPES = [
  { value: 'bug', label: 'Bug Report', icon: Bug, color: 'text-red-400' },
  { value: 'idea', label: 'Feature Idea', icon: Lightbulb, color: 'text-amber-400' },
  { value: 'question', label: 'Question', icon: HelpCircle, color: 'text-blue-400' },
  { value: 'praise', label: 'Praise', icon: Heart, color: 'text-pink-400' },
];

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('bug');
  const [severity, setSeverity] = useState('medium');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!message.trim()) { toast.error('Please enter your feedback'); return; }
    setSending(true);
    try {
      await feedbackAPI.submit({
        type, severity, message: message.trim(),
        page_url: window.location.pathname,
        user_agent: navigator.userAgent,
      });
      setSent(true);
      setTimeout(() => { setOpen(false); setSent(false); setMessage(''); }, 2000);
    } catch (e) {
      toast.error('Failed to submit feedback');
    }
    setSending(false);
  };

  return (
    <>
      {/* Side tab button — right edge, vertically centered */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="feedback-widget-btn"
          className="fixed right-0 top-1/2 -translate-y-1/2 z-50 w-8 h-8 rounded-l-lg bg-primary text-primary-foreground shadow-md flex items-center justify-center hover:w-9 active:scale-95 transition-all"
          title="Send Feedback"
        >
          <MessageSquare className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: 20, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed top-1/2 -translate-y-1/2 right-3 z-50 w-[min(340px,calc(100vw-24px))] rounded-xl border border-border bg-card shadow-2xl overflow-hidden"
            data-testid="feedback-widget-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Send Feedback</h3>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>

            {sent ? (
              <div className="p-8 text-center">
                <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm font-medium">Thank you!</p>
                <p className="text-xs text-muted-foreground mt-1">Your feedback has been submitted.</p>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {/* Type selector */}
                <div className="flex gap-1.5">
                  {TYPES.map(t => (
                    <button
                      key={t.value}
                      onClick={() => setType(t.value)}
                      className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg border transition-colors text-[10px] ${
                        type === t.value ? 'border-primary/40 bg-primary/5' : 'border-transparent hover:bg-muted/30'
                      }`}
                    >
                      <t.icon className={`w-4 h-4 ${type === t.value ? t.color : 'text-muted-foreground'}`} />
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Severity */}
                {type === 'bug' && (
                  <Select value={severity} onValueChange={setSeverity}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                )}

                {/* Message */}
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={type === 'bug' ? 'Describe the bug you encountered...' : type === 'idea' ? 'Describe your feature idea...' : 'Your feedback...'}
                  className="text-xs min-h-[80px]"
                  rows={4}
                  data-testid="feedback-message-input"
                />

                <Button onClick={submit} disabled={sending} className="w-full text-xs h-8" data-testid="feedback-submit-btn">
                  {sending ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending...</> : <><Send className="w-3 h-3 mr-1" /> Submit Feedback</>}
                </Button>

                <p className="text-[10px] text-muted-foreground text-center">Your current page and browser info will be included automatically.</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
